import { useState, type FormEvent } from "react";

const FORM_ACTION = "https://readdy.ai/api/form/dailrcjp14a1h3mr76kg";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value as string);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  return (
    <div id="contact" className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
        反馈与投稿
      </h3>
      <p className="mt-2 text-sm leading-7 text-foreground-600">
        发现内容有误，或想推荐一条值得收录的热点？留下你的信息，我们会认真查看每一条反馈。
      </p>

      <form data-readdy-form onSubmit={onSubmit} className="mt-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800">
              昵称
            </label>
            <input
              id="fb-name"
              name="nickname"
              type="text"
              required
              placeholder="怎么称呼你"
              className="mt-2 w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <div>
            <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800">
              邮箱
            </label>
            <input
              id="fb-email"
              name="email"
              type="email"
              required
              placeholder="用于回复你的反馈"
              className="mt-2 w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
        </div>

        <div>
          <label htmlFor="fb-type" className="block text-sm font-medium text-foreground-800">
            反馈类型
          </label>
          <select
            id="fb-type"
            name="type"
            required
            defaultValue="内容纠错"
            className="mt-2 w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors cursor-pointer"
          >
            <option value="内容纠错">内容纠错</option>
            <option value="热点推荐">热点推荐</option>
            <option value="功能建议">功能建议</option>
            <option value="其他反馈">其他反馈</option>
          </select>
        </div>

        <div>
          <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-800">
            反馈内容
          </label>
          <textarea
            id="fb-message"
            name="message"
            rows={5}
            required
            maxLength={500}
            placeholder="请简单描述你遇到的问题或想推荐的内容（最多 500 字）"
            className="mt-2 w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
          ></textarea>
        </div>

        <input
          type="text"
          name="company_alt"
          className="field-assist-off"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中...
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交反馈
            </>
          )}
        </button>

        {status === "success" && (
          <p className="flex items-center gap-2 text-sm text-secondary-700">
            <i className="ri-checkbox-circle-line"></i>
            已收到你的反馈，感谢你的支持，我们会尽快查看。
          </p>
        )}
        {status === "error" && (
          <p className="flex items-center gap-2 text-sm text-primary-700">
            <i className="ri-error-warning-line"></i>
            {formError}
          </p>
        )}
      </form>
    </div>
  );
}